import { defineStore } from 'pinia'
import { login, logout, getUserInfo } from '@/api/auth'
import { getToken, setToken as setAuthToken, removeToken, getRefreshToken, setRefreshToken, removeRefreshToken, setUser, getUser } from '@/utils/auth'
import { useSettingsStore } from './settings'

export const useUserStore = defineStore('user', {
  state: () => ({
    token: getToken(),
    refreshToken: getRefreshToken(),
    id: '',
    username: '',
    realname: '',
    email: '',
    phone: '',
    avatar: '',
    sex: '',
    deptId: '',
    roles: [],
    permissions: [],
    // 组织信息
    orgId: '',
    orgCode: '',
    orgName: '',
    orgLevel: '',
    orgPath: ''
  }),

  getters: {
    // 是否有管理员权限
    isAdmin: (state) => {
      return state.roles.includes('admin')
    },

    // 获取用户显示名称
    displayName: (state) => {
      return state.realname || state.username
    },

    // 获取组织显示信息（组织代码-组织名称）
    orgDisplayName: (state) => {
      if (state.orgCode && state.orgName) {
        return `${state.orgCode}-${state.orgName}`
      } else if (state.orgName) {
        return state.orgName
      } else if (state.orgCode) {
        return state.orgCode
      }
      return ''
    },

    // 获取完整的组织信息
    fullOrgInfo: (state) => {
      return {
        id: state.orgId,
        code: state.orgCode,
        name: state.orgName,
        level: state.orgLevel,
        path: state.orgPath
      }
    },

    // 检查是否有指定权限
    hasPermission: (state) => (permission) => {
      return state.permissions.includes('*:*:*') ||
             state.permissions.includes(permission) ||
             state.permissions.some(p => {
               // 支持通配符匹配，如 system:user:* 匹配 system:user:list
               if (p.includes('*')) {
                 const regex = new RegExp(p.replace(/\*/g, '.*'))
                 return regex.test(permission)
               }
               return p === permission
             })
    }
  },

  actions: {
    // 登录
    async login(loginForm) {
      try {
        const { data } = await login(loginForm)
        const token = data.accessToken || data.token
        const rtToken = data.refreshToken

        this.token = token
        setAuthToken(token)

        if (rtToken) {
          this.refreshToken = rtToken
          setRefreshToken(rtToken)
        }

        return Promise.resolve(data)
      } catch (error) {
        return Promise.reject(error)
      }
    },

    // 设置用户信息
    setUserInfo(userInfo) {
      this.id = userInfo.userId
      this.username = userInfo.username
      this.realname = userInfo.realname
      this.email = userInfo.email
      this.phone = userInfo.phone
      this.avatar = userInfo.avatar
      this.sex = userInfo.sex || ''
      this.deptId = userInfo.deptId

      // 设置组织信息
      this.orgId = userInfo.orgId || ''
      this.orgCode = userInfo.orgCode || ''
      this.orgName = userInfo.orgName || ''
      this.orgLevel = userInfo.orgLevel || ''
      this.orgPath = userInfo.orgPath || ''

      // 处理角色和权限 - 为admin用户设置默认角色
      if (userInfo.roles && userInfo.roles.trim() && userInfo.roles !== '') {
        this.roles = userInfo.roles.split(',')
      } else {
        // 如果没有角色信息，为admin用户设置默认角色
        this.roles = ['admin']
      }

      if (userInfo.permissions && userInfo.permissions.trim() && userInfo.permissions !== '') {
        this.permissions = userInfo.permissions.split(',')
      } else {
        this.permissions = []
      }

      // 同步到 localStorage，供 utils/auth.js 的 hasAnyPermission 使用
      setUser({
        id: this.id,
        username: this.username,
        realname: this.realname,
        email: this.email,
        phone: this.phone,
        avatar: this.avatar,
        deptId: this.deptId,
        roles: this.roles,
        permissions: this.permissions,
        orgId: this.orgId,
        orgCode: this.orgCode,
        orgName: this.orgName,
        orgLevel: this.orgLevel,
        orgPath: this.orgPath
      })
    },

    // 设置Token
    setUserToken(token) {
      this.token = token
      setAuthToken(token)
    },

    // 获取用户信息
    async getInfo() {
      try {
        const { data } = await getUserInfo()

        const { id, username, realname, email, phone, avatar, sex, deptId, roles, permissions } = data

        this.id = id
        this.username = username
        this.realname = realname
        this.email = email
        this.phone = phone
        this.avatar = avatar
        this.sex = sex || ''
        this.deptId = deptId

        // 设置组织信息
        this.orgId = data.orgId || ''
        this.orgCode = data.orgCode || ''
        this.orgName = data.orgName || ''
        this.orgLevel = data.orgLevel || ''
        this.orgPath = data.orgPath || ''

        // 处理角色 - 将字符串转换为数组
        if (roles && typeof roles === 'string' && roles.length > 0) {
          this.roles = roles.split(',').filter(r => r.trim())
        } else if (roles && Array.isArray(roles) && roles.length > 0) {
          this.roles = roles
        } else {
          this.roles = ['admin']
        }

        // 处理权限 - 将字符串转换为数组
        if (permissions && typeof permissions === 'string' && permissions.length > 0) {
          this.permissions = permissions.split(',').filter(p => p.trim())
        } else if (permissions && Array.isArray(permissions) && permissions.length > 0) {
          this.permissions = permissions
        } else {
          this.permissions = []
        }

        // 同步到 localStorage，供 utils/auth.js 的 hasAnyPermission 使用
        setUser({
          id: this.id,
          username: this.username,
          realname: this.realname,
          email: this.email,
          phone: this.phone,
          avatar: this.avatar,
          deptId: this.deptId,
          roles: this.roles,
          permissions: this.permissions,
          orgId: this.orgId,
          orgCode: this.orgCode,
          orgName: this.orgName,
          orgLevel: this.orgLevel,
          orgPath: this.orgPath
        })

        return Promise.resolve(data)
      } catch (error) {
        // 如果获取用户信息失败，设置默认值
        this.roles = []
        this.permissions = []
        // 设置默认组织信息
        this.orgId = '1'
        this.orgCode = 'HQ'
        this.orgName = '总公司'
        this.orgLevel = '1'
        this.orgPath = '/1/'
        console.warn('获取用户信息失败，使用默认权限和组织:', error)
        // 设置默认权限
        this.roles = []
        this.permissions = []
        // 设置默认组织信息
        this.orgId = '1'
        this.orgCode = 'HQ'
        this.orgName = '总公司'
        this.orgLevel = '1'
        this.orgPath = '/1/'

        // 同步到 localStorage
        setUser({
          id: this.id,
          username: this.username,
          roles: this.roles,
          permissions: this.permissions,
          orgId: this.orgId,
          orgCode: this.orgCode,
          orgName: this.orgName,
          orgLevel: this.orgLevel,
          orgPath: this.orgPath
        })

        return Promise.resolve({
          id: this.id,
          username: this.username,
          roles: this.roles,
          permissions: this.permissions,
          orgId: this.orgId,
          orgCode: this.orgCode,
          orgName: this.orgName,
          orgLevel: this.orgLevel,
          orgPath: this.orgPath
        })
      }
    },

    // 登出
    async logout() {
      // 无论后端调用是否成功，都清理本地状态
      const clearLocalState = () => {
        this.token = ''
        this.refreshToken = ''
        this.id = ''
        this.username = ''
        this.realname = ''
        this.email = ''
        this.phone = ''
        this.avatar = ''
        this.sex = ''
        this.deptId = ''
        this.roles = []
        this.permissions = []
        // 清除组织信息
        this.orgId = ''
        this.orgCode = ''
        this.orgName = ''
        this.orgLevel = ''
        this.orgPath = ''
        removeToken()
        removeRefreshToken()
        // 清除标签页和缓存视图
        const settingsStore = useSettingsStore()
        settingsStore.clearAllTags()
      }

      try {
        await logout()
      } catch (error) {
        // 即使后端登出失败，也清理本地状态（Token可能已过期）
        console.warn('后端登出调用失败，继续清理本地状态:', error.message)
      } finally {
        clearLocalState()
      }

      return Promise.resolve()
    },

    // 重置token
    resetToken() {
      this.token = ''
      this.refreshToken = ''
      this.id = ''
      this.username = ''
      this.realname = ''
      this.email = ''
      this.phone = ''
      this.avatar = ''
      this.sex = ''
      this.deptId = ''
      this.roles = []
      this.permissions = []
      // 清除组织信息
      this.orgId = ''
      this.orgCode = ''
      this.orgName = ''
      this.orgLevel = ''
      this.orgPath = ''
      removeToken()
      removeRefreshToken()
      // 清除标签页和缓存视图
      const settingsStore = useSettingsStore()
      settingsStore.clearAllTags()
    },

    // 更新用户信息
    updateUserInfo(userInfo) {
      Object.assign(this, userInfo)
    }
  }
})